'use client';

import { useToastState } from '@lib/context/toast-context';
import { cn } from '@lib/util/cn';
import { CheckCircle } from '@modules/common/icons';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

const TOAST_DURATION_MS = 4000;

type ToastContainerProps = {
  className?: string;
  itemClassName?: string;
};

function ToastItem({
  id,
  message,
  duration,
  onDismiss,
  className,
}: {
  id: number;
  message: string;
  duration?: number;
  onDismiss: (id: number) => void;
  className?: string;
}) {
  const toastDuration = duration ?? TOAST_DURATION_MS;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => onDismiss(id), toastDuration);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [id, onDismiss, toastDuration]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        'relative overflow-hidden rounded-[8px] shadow-[0px_0px_4px_rgba(0,0,0,0.04),0px_4px_8px_rgba(0,0,0,0.06)]',
        className,
      )}
      style={{ backgroundColor: '#17f0de' }}
    >
      <div className="flex items-center gap-4 px-4 py-3">
        <span
          className="nw-typography-paragraph-xs font-medium flex-1"
          style={{ color: '#333830' }}
          translate="no"
        >
          {message}
        </span>
        <CheckCircle
          size="20"
          style={{ color: '#333830' }}
          aria-hidden="true"
        />
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px]">
        <div
          className="h-full w-full animate-toast-progress"
          style={{
            backgroundColor: '#09b9ab',
            transformOrigin: 'left',
            animationDuration: `${toastDuration}ms`,
          }}
        />
      </div>
    </div>
  );
}

export function ToastContainer({
  className,
  itemClassName,
}: ToastContainerProps) {
  const { toasts, dismiss, clearAll } = useToastState();
  const pathname = usePathname();

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is a navigation trigger — effect must re-run on route change
  useEffect(() => {
    clearAll();
  }, [pathname, clearAll]);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Notifications"
      className={cn('flex flex-col gap-2', className)}
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          message={toast.message}
          duration={toast.duration}
          onDismiss={dismiss}
          className={itemClassName}
        />
      ))}
    </section>
  );
}
